import { useState, useMemo, useEffect } from "react"
import { FileText, AlertTriangle, Gamepad2 } from "lucide-react"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import { AnalyticsStats } from "../components/ui/school/analytics/analytics-stats"
import { TermCountdown } from "../components/ui/school/analytics/term-countdown"
import { CareerPathChart } from "../components/ui/school/analytics/career-path-chart"
import { ClassProgressOverview } from "../components/ui/school/analytics/class-progress-overview"
import { analyticsData } from "../lib/analytics-data"
import { StorageService } from "@/hooks/services/storageService"
import { buildStudentSummary } from "@/hooks/useStudentSummary"
import { useStudentAnalytics } from "@/hooks/useStudentAnalytics"
import { generateReport } from "@/hooks/useReportGenerator"
import { ReportModal } from "@/components/ui/report/report-modal"
import { getSchoolStats, listStudents } from "@/hooks/api/schoolApi"
import config from "../../config"

// ─── Path name normaliser ─────────────────────────────────────────────────────
const PATH_LABELS = {
  broad_stem_tech: "STEM & Tech",
  broad_health_medicine: "Health",
  broad_creative_arts: "Arts",
  broad_business_finance: "Business",
  broad_law_social: "Law & Social",
}

const PATH_COLORS = {
  broad_stem_tech: "#21a193",
  broad_health_medicine: "#4F46E5",
  broad_creative_arts: "#f59e0b",
  broad_business_finance: "#2DD4BF",
  broad_law_social: "#A78BFA",
}

// ─── Compute aggregate analytics from all students ────────────────────────────
function useSchoolAnalytics(allStudents) {
  return useMemo(() => {
    if (!allStudents.length) {
      return {
        totalStudents: 0,
        avgScore: 0,
        taskCompletion: 0,
        engagement: "Low",
        careerPathData: [],
        classProgressData: [],
      }
    }

    const totalStudents = allStudents.length

    // Avg score: average across all students' subjectStrengths
    const allScores = allStudents.flatMap((s) => {
      const subjectData = s.traitEvents
        ?.filter((e) => e.source === "quiz" || e.source === "exam_result")
        .map((e) => e.meta?.score)
        .filter((sc) => typeof sc === "number") ?? []
      return subjectData
    })
    const avgScore = allScores.length
      ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length)
      : 72

    // Task completion: % of students with at least 1 completed goal
    const withGoals = StorageService.read("goals") ?? []
    const completedGoalUserIds = new Set(
      withGoals.filter((g) => g.status === "completed").map((g) => g.userId)
    )
    const studentUserIds = allStudents.map((s) => s.userId ?? s._id)
    const taskCompletion = studentUserIds.length
      ? Math.round((studentUserIds.filter((id) => completedGoalUserIds.has(id)).length / studentUserIds.length) * 100)
      : 0

    // Engagement: loginFrequency per week average
    const allSessions = allStudents.reduce((acc, s) => {
      const freq = Array.isArray(s.engagementData)
        ? s.engagementData.reduce((sum, week) => sum + (week.sessions ?? 0), 0)
        : (s.engagementData?.loginFrequency ?? 0)
      return acc + freq
    }, 0)
    const avgSessions = totalStudents > 0 ? allSessions / totalStudents : 0
    const engagement = avgSessions >= 5 ? "High" : avgSessions >= 2 ? "Medium" : "Low"

    // Career path distribution
    const pathCounts = {}
    allStudents.forEach((s) => {
      const paths = s.selectedBroadPaths ?? s.activeNodes?.map((n) => n.pathId) ?? []
      paths.forEach((p) => { pathCounts[p] = (pathCounts[p] ?? 0) + 1 })
    })
    const maxCount = Math.max(1, ...Object.values(pathCounts))
    const careerPathData = Object.entries(pathCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([pathId, count]) => ({
        name: PATH_LABELS[pathId] ?? pathId,
        percentage: Math.round((count / maxCount) * 100),
        color: PATH_COLORS[pathId] ?? "#6366f1",
      }))

    // Class progress: aggregate subject scores per student
    const subjectTotals = {}
    const subjectCounts = {}
    allStudents.forEach((s) => {
      const traitEvents = s.traitEvents ?? []
      traitEvents.forEach((e) => {
        if ((e.source === "quiz" || e.source === "exam_result") && e.meta?.subject) {
          const subj = e.meta.subject
          subjectTotals[subj] = (subjectTotals[subj] ?? 0) + (e.meta?.score ?? 0)
          subjectCounts[subj] = (subjectCounts[subj] ?? 0) + 1
        }
      })
    })
    const ICONS = ["BookOpen", "Calculator", "Beaker", "Palette"]
    const COLORS = ["#21a193", "#4F46E5", "#f59e0b", "#A78BFA"]
    const classProgressData = Object.entries(subjectTotals)
      .map(([subject, total], i) => ({
        subject,
        grade: "All Grades",
        module: "Aggregated",
        progress: Math.round(total / subjectCounts[subject]),
        icon: ICONS[i % ICONS.length],
        color: COLORS[i % COLORS.length],
      }))
      .slice(0, 5)

    return { totalStudents, avgScore, taskCompletion, engagement, careerPathData, classProgressData }
  }, [allStudents])
}

// ─── Thin wrapper that computes analytics for a chosen student ────────────────
function useAdminReport(student) {
  const analytics = useStudentAnalytics(student?.userId ?? student?._id ?? "")
  return analytics
}

// ─── At-risk panel ────────────────────────────────────────────────────────────
function AtRiskPanel({ students }) {
  const cutoff = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
  const atRisk = students.filter((s) => {
    const last = s.metadata?.lastLoginAt ?? s.updatedAt
    return !last || last < cutoff
  }).slice(0, 5)

  if (atRisk.length === 0) return null
  return (
    <div className="bg-white rounded-2xl border border-amber-100 p-5 space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        <h3 className="font-bold text-slate-900 text-sm">At-Risk Students</h3>
        <span className="ml-auto text-xs text-slate-400">No activity in 14+ days</span>
      </div>
      {atRisk.map((s) => (
        <div key={s._id ?? s.id} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center text-xs font-bold text-amber-600">
            {(s.firstName ?? s.name ?? "?")[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">{s.firstName ?? s.name} {s.lastName ?? ""}</p>
            <p className="text-xs text-slate-400">{s.classLevel ?? s.class ?? "—"}</p>
          </div>
          <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-2 py-0.5 rounded-full">Inactive</span>
        </div>
      ))}
    </div>
  )
}

// ─── Game engagement panel ────────────────────────────────────────────────────
const GAME_NAMES = {
  equation_scape: "EquationScape",
  fix_it_challenge: "Fix It",
  naira_tycoon: "Naira Tycoon",
  word_smith: "WordSmith",
  compound_connector: "Compound Connector",
  biology_kingdom: "Biology Kingdom",
  budget_blitz: "Budget Blitz",
  capital_scramble: "Capital Scramble",
  civic_spin: "Civic Spin",
  match_game: "Match Game",
}

function GameEngagementPanel({ students }) {
  const gameCounts = useMemo(() => {
    const counts = {}
    students.forEach((s) => {
      (s.activityLog ?? [])
        .filter((e) => e.eventType === "game_played")
        .forEach((e) => {
          const id = e.meta?.gameId ?? "unknown"
          counts[id] = (counts[id] ?? 0) + 1
        })
    })
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5)
  }, [students])

  if (gameCounts.length === 0) return null
  const max = gameCounts[0]?.[1] ?? 1

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Gamepad2 className="w-4 h-4 text-indigo-500" />
        <h3 className="font-bold text-slate-900 text-sm">Game Engagement</h3>
      </div>
      {gameCounts.map(([gameId, count]) => (
        <div key={gameId}>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-semibold text-slate-700">{GAME_NAMES[gameId] ?? gameId}</span>
            <span className="text-slate-400">{count} plays</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-400 rounded-full"
              style={{ width: `${Math.round((count / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

const AnalyticsPage = () => {
  const [selectedStudentId, setSelectedStudentId] = useState("")
  const [report, setReport] = useState(null)
  const [apiStats, setApiStats] = useState(null)
  const [apiStudents, setApiStudents] = useState([])

  const localStudents = useMemo(() => StorageService.read("students") ?? [], [])
  const allStudents = apiStudents.length > 0 ? apiStudents : localStudents

  useEffect(() => {
    if (config.appMode === "prototype") return
    getSchoolStats().then(setApiStats).catch(() => {})
    listStudents({ limit: 200 })
      .then(({ students }) => { if (students?.length) setApiStudents(students) })
      .catch(() => {})
  }, [])

  const selectedStudent = allStudents.find((s) => (s._id ?? s.id) === selectedStudentId) ?? allStudents[0]
  const analytics = useAdminReport(selectedStudent)
  const schoolStats = useSchoolAnalytics(allStudents)

  const handleGenerateReport = () => {
    if (!selectedStudent) return
    const summary = buildStudentSummary(selectedStudent)
    setReport(generateReport(summary, analytics, "school", selectedStudent.gender))
  }

  return (
    <SchoolLayout>
      <div className="overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-transparent border-b border-transparent px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">Analytics Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Comprehensive insights and performance metrics</p>
            </div>
            {/* Student report generator */}
            {allStudents.length > 0 && (
              <div className="flex items-center gap-2">
                <select
                  value={selectedStudentId || (selectedStudent?._id ?? selectedStudent?.id) || ""}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  {allStudents.map((s) => (
                    <option key={s._id ?? s.id} value={s._id ?? s.id}>
                      {s.firstName ?? s.name} {s.lastName ?? ""}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleGenerateReport}
                  className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-3 py-2 rounded-lg transition-all active:scale-95"
                >
                  <FileText className="w-4 h-4" />
                  Student Report
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="p-4 md:p-6 lg:p-8 space-y-8">
          {/* Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Stats Grid — real data (API preferred) */}
            <div className="lg:col-span-8">
              <AnalyticsStats
                totalStudents={apiStats?.totalStudents ?? schoolStats.totalStudents ?? allStudents.length}
                avgScore={schoolStats.avgScore || analyticsData.avgScore}
                taskCompletion={schoolStats.taskCompletion || analyticsData.taskCompletion}
                engagement={schoolStats.engagement || analyticsData.engagement}
              />
            </div>

            {/* Term Countdown — kept static as school-defined */}
            <div className="lg:col-span-4">
              <TermCountdown
                daysLeft={analyticsData.daysLeft}
                hoursLeft={analyticsData.hoursLeft}
                termProgress={analyticsData.termProgress}
              />
            </div>
          </div>

          {/* Career Path & Class Progress — real data */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              <CareerPathChart
                data={schoolStats.careerPathData.length > 0 ? schoolStats.careerPathData : [
                  { name: "STEM & Tech", percentage: 65, color: "#21a193" },
                  { name: "Arts", percentage: 45, color: "#f59e0b" },
                  { name: "Business", percentage: 55, color: "#2DD4BF" },
                  { name: "Health", percentage: 30, color: "#4F46E5" },
                  { name: "Law", percentage: 25, color: "#A78BFA" },
                ]}
              />
            </div>
            <div className="lg:col-span-5">
              <ClassProgressOverview
                data={schoolStats.classProgressData.length > 0 ? schoolStats.classProgressData : [
                  { subject: "Mathematics", grade: "All Grades", module: "Aggregated", progress: 72, icon: "Calculator", color: "#4F46E5" },
                  { subject: "Biology", grade: "All Grades", module: "Aggregated", progress: 68, icon: "Beaker", color: "#21a193" },
                  { subject: "English", grade: "All Grades", module: "Aggregated", progress: 80, icon: "BookOpen", color: "#f59e0b" },
                ]}
              />
            </div>
          </div>

          {/* At-risk + Game engagement */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AtRiskPanel students={allStudents} />
            <GameEngagementPanel students={allStudents} />
          </div>
        </div>
      </div>

      {report && <ReportModal report={report} onClose={() => setReport(null)} />}
    </SchoolLayout>
  )
}

export default AnalyticsPage
